<template>
  <template v-if="!product.title.includes('E-Gift Card')">
    <div v-if="selectedVariant">
      <button
        v-if="selectedVariant.available"
        data-add-to-cart
        class="btn btn--secondary btn--medium pm__atc"
        @click="atc()"
        :disabled="isATCDisabled"
      >
        ADD TO CART
      </button>
      <div v-else>
        <button
          v-if="!selectedFirstOption.bis"
          data-add-to-cart
          disabled
          class="btn btn--secondary btn--medium pm__atc"
        >
          OUT OF STOCK
        </button>
        <button v-else class="btn btn--secondary btn--medium pm__atc" @click="bisDrawer">
          Notify Me When Available
        </button>
      </div>
    </div>
    <div v-else>
      <button
        data-add-to-cart
        @mouseover="mouseOverUnselectBtn = true"
        @mouseleave="mouseOverUnselectBtn = false"
        class="btn btn--secondary btn--medium pm__atc"
      >
        <span v-if="!mouseOverUnselectBtn">ADD TO CART</span>
        <span v-else>SELECT {{ unselectBtnName }}</span>
      </button>
    </div>
  </template>
  <template v-else>
    <div class="recipient__wrapper">
      <div class="recipient__input-wrapper">
        <input
          :checked="giftCardData.isSendToRecipient"
          @change="handleFieldChange('isSendToRecipient', $event.target.checked)"
          type="checkbox"
          id="recipientCheck"
          class="recipient__input"
        />
        <label for="recipientCheck" class="recipient__label">I want to send this as a gift</label>
      </div>
      <form v-if="giftCardData.isSendToRecipient" class="recipient__info" @submit.prevent="recipientAtc">
        <label data-name="Recipient Name">
          Recipient Name <span class="required__field">*</span>
          <input 
            :value="giftCardData.recipientName" 
            @input="handleFieldChange('recipientName', $event.target.value)"
            type="text" 
            placeholder="Recipient Name" 
            required 
          />
        </label>
        <label data-name="Recipient Email">
          Recipient Email <span class="required__field">*</span>
          <input
            :value="giftCardData.recipientEmail"
            @input="handleFieldChange('recipientEmail', $event.target.value)"
            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
            type="email"
            placeholder="Recipient Email"
            required
          />
        </label>
        <label data-name="Message">
          Message (Optional)
          <textarea 
            :value="giftCardData.message"
            @input="handleFieldChange('message', $event.target.value)"
            rows="10" 
            maxlength="200" 
            placeholder="Message"
          ></textarea>
        </label>
        <label data-name="Date To Send">
          Date To Send (Optional)
          <input
            :value="giftCardData.sendDate"
            @change="handleFieldChange('sendDate', $event.target.value)"
            :min="minDate"
            type="date"
            placeholder="Date To Send (Optional)"
            data-date-format="DD MMMM YYYY"
          />
        </label>
        <button 
          type="submit" 
          data-add-to-cart 
          class="btn btn--secondary btn--medium pm__atc"
        >
          ADD TO CART
        </button>
      </form>
      <button v-else data-add-to-cart class="btn btn--secondary btn--medium pm__atc" @click="atc()">ADD TO CART</button>
    </div>
  </template>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted } from 'vue';
  import { useStore } from 'vuex';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },

    productForm: {
      type: Object,
      default: () => ({}),
    },

    selectedVariant: {
      type: Object,
      default: () => ({}),
    },

    selectedFirstOption: {
      type: Object,
      default: () => ({}),
    },

    isPDPstickyBar: {
      type: Boolean,
      default: false,
    },
  });

  const store = useStore();
  const { product, productForm, selectedVariant, selectedFirstOption, isPDPstickyBar } = toRefs(props);
  let mouseOverUnselectBtn = ref(false);
  const emit = defineEmits(['product-added', 'on-atc-state-change']);
  const cartItems = ref(window.cartItems);

  onMounted(() => {
    window.addEventListener('cartItemsUpdated', updateCartItems);
  });

  const giftCardData = computed(() => store.getters.getGiftCardReceipient);

  const unselectBtnName = computed(() => {
    const { options } = product.value;
    let name;
    for (let option in options) {
      if (!productForm.value[options[option].name]) {
        name = options[option].name;
        break;
      }
    }
    return name;
  });

  const minDate = computed(() => {
    return new Date().toISOString().split('T')[0];
  });

  const isATCDisabled = computed(() => {
    const { id, inventory_quantity } = selectedVariant.value;
    let flag = false;

    if (id && cartItems.value?.length) {
      const selCartItem = cartItems.value.find((item) => item.id == id);
      flag = selCartItem?.quantity === inventory_quantity;
    }

    emit('on-atc-state-change', flag);
    return flag;
  });

  const updateCartItems = () => {
    cartItems.value = window.cartItems;
  };

  const bisDrawer = () => {
    window.toggleDrawer(true, 'BISModal', {
      productId: product.value.id,
      variantId: selectedVariant.value.id,
      bisDate: selectedFirstOption.value.bisDate,
    });
  };

  const atc = async () => {
    if (isPDPstickyBar.value) {
      emit('product-added');
    }
    window.openCart({ cartConfirmation: true });
    await window.Additem({
      id: selectedVariant.value.id,
      quantity: 1,
      properties: {
        ...(product.value.final_sale_msg && { Note: product.value.final_sale_msg }),
      },
    });
  };

  const recipientAtc = async () => {
    if (isPDPstickyBar.value) {
      emit('product-added');
    }
    window.openCart({ cartConfirmation: true });
    await window.Additem({
      id: selectedVariant.value.id,
      quantity: 1,
      properties: {
        ...(product.value.final_sale_msg && { Note: product.value.final_sale_msg }),
        __shopify_send_gift_card_to_recipient: 'on',
        'Recipient name': giftCardData.value.recipientName,
        'Recipient email': giftCardData.value.recipientEmail,
        ...(giftCardData.value.message.length && { Message: giftCardData.value.message }),
        'Send on': giftCardData.value.sendDate,
      },
    });

    store.dispatch('resetGiftCardForm');

    // This is for ADA task i.e retaining focus on checkbox.
    if (product.value?.title?.includes('E-Gift Card') && giftCardData.value.isSendToRecipient === false) {
      const recipientCheckbox = document.querySelector('#recipientCheck');
      recipientCheckbox?.focus();
    }
  };

  // Central function to handle all field changes
  const handleFieldChange = (fieldName, value) => {    
    // Dispatch to store using the generic updateGiftCardReceipient action
    switch (fieldName) {
      case 'recipientName':
        store.dispatch('updateGiftCardReceipient', { key: 'recipientName', value });
        break;
      case 'recipientEmail':
        store.dispatch('updateGiftCardReceipient', { key: 'recipientEmail', value });
        break;
      case 'message':
        store.dispatch('updateGiftCardReceipient', { key: 'message', value });
        break;
      case 'sendDate':
        store.dispatch('updateGiftCardReceipient', { key: 'sendDate', value });
        break;
      case 'isSendToRecipient':
        store.dispatch('updateGiftCardReceipient', { key: 'isSendToRecipient', value });
        break;
      default:
        console.warn(`Unknown field: ${fieldName}`);
    }
  };
</script>
